import { useMutation, useQuery } from '@apollo/client';
import { message } from 'antd';
import { useState } from 'react';
import {
  CasesCredentialVerificationCaseDetailsContainerCredentialVerificationCaseV1CaseWorkerDocument,
  CasesCredentialVerificationCaseDetailsContainerCredentialVerificationCaseV1Document,
  CasesCredentialVerificationCaseDetailsContainerCredentialVerificationCaseV1UpdateDocument,
  CasesCredentialVerificationCaseDetailsContainerCredentialVerificationCaseV1UpdateMutationVariables,
  CasesCredentialVerificationCaseDetailsContainerStaffUsersDocument,
  CasesCredentialVerificationV1CaseDetailsContainerStaffUserCurrentDocument,
  StaffUser
} from '../../../../../../generated';
import { useOid } from '../../../../../../hooks/useOid';
import { CaseDetails } from '../../../../../shared/case-details/case-details';
import { ComponentQueryLoader } from '../../../../../shared/component-query-loader';
import { getStaffUserCurrentQueryOptionsWithOidAndCacheEnabled } from '../../../../../shared/utils/queryOptions';

interface CaseDetailsContainerProps {
  caseId: string;
}

export const CaseDetailsContainer: React.FC<CaseDetailsContainerProps> = (props) => {
  const oid = useOid();
  const staffUserCurrentQueryOptions = getStaffUserCurrentQueryOptionsWithOidAndCacheEnabled(oid);

  const [assigndWorker, setAssignedWorker] = useState<StaffUser | undefined>(undefined);
  // Query to get the staff data
  const {
    data: staffData,
    loading: staffLoading,
    error: staffError
  } = useQuery(CasesCredentialVerificationCaseDetailsContainerStaffUsersDocument, { context: { headers: { 'cache-enabled': 'true' } } });

  const {
    data: currentStaffData,
    loading: currentStaffLoading,
    error: currentStaffError
  } = useQuery(CasesCredentialVerificationV1CaseDetailsContainerStaffUserCurrentDocument, staffUserCurrentQueryOptions);

  const {
    data: caseStatusData,
    loading: caseStatusLoading,
    error: caseStatusError
  } = useQuery(CasesCredentialVerificationCaseDetailsContainerCredentialVerificationCaseV1Document, {
    variables: {
      credentialVerificationCaseV1Id: props.caseId
    }
  });

  const {
    data: assignedWorkerData,
    loading: assignedWorkerLoading,
    error: assignedWorkerError
  } = useQuery(CasesCredentialVerificationCaseDetailsContainerCredentialVerificationCaseV1CaseWorkerDocument, {
    variables: {
      credentialVerificationCaseV1Id: props.caseId
    },
    onCompleted: (data) => {
      setAssignedWorker(data.credentialVerificationCaseV1?.caseDetails?.applicationReview?.caseWorkerAssigned as StaffUser);
    }
  });

  const [tagsUpdate] = useMutation(CasesCredentialVerificationCaseDetailsContainerCredentialVerificationCaseV1UpdateDocument, {});

  const [caseWorkerUpdate] = useMutation(CasesCredentialVerificationCaseDetailsContainerCredentialVerificationCaseV1UpdateDocument, {
    onCompleted: (data) => {
      // Update the local state with the new assigned workwe
      setAssignedWorker(data?.credentialVerificationCaseV1Update?.credentialVerificationCaseV1?.caseDetails?.applicationReview?.caseWorkerAssigned as StaffUser);
      if (data?.credentialVerificationCaseV1Update?.status.success) {
        message.success('Assignment saved successfully');
      } else {
        message.error(`Error saving Assignment: ${data?.credentialVerificationCaseV1Update?.status.errorMessage}`);
      }
    }
  });

  const staffUsers =
    staffData?.staffUsers
      ?.filter((item): item is StaffUser => item !== null && item !== undefined)
      .filter((staff) => staff.role?.permissions?.credCasePermissions?.canBeAssignedCases) || [];

  // Function to handle the save action
  const handleSave = async (data: StaffUser | null) => {
    try {
      const credentialVerificationUpdateVariables: CasesCredentialVerificationCaseDetailsContainerCredentialVerificationCaseV1UpdateMutationVariables = {
        input: {
          id: props.caseId,
          caseDetails: {
            applicationReview: {
              caseWorkerAssigned: data?.id || null
            }
          }
        }
      };
      await caseWorkerUpdate({
        variables: credentialVerificationUpdateVariables
      });
    } catch (error) {
      message.error(`Error saving Assignment: ${JSON.stringify(error)}`);
    }
  };

  const handleTagsChange = async (newTags: string[]): Promise<boolean> => {
    try {
      const credentialVerificationUpdateVariables: CasesCredentialVerificationCaseDetailsContainerCredentialVerificationCaseV1UpdateMutationVariables = {
        input: {
          id: props.caseId,
          tags: newTags
        }
      };
      const result = await tagsUpdate({
        variables: credentialVerificationUpdateVariables
      });
      if (!result.data?.credentialVerificationCaseV1Update?.status.success) {
        message.error(`Error removing tags : ${result.data?.credentialVerificationCaseV1Update?.status.errorMessage}`);
        return false;
      } else {
        message.success('tags updated successfully');
        return true;
      }
    } catch (error) {
      message.error(`Error saving tags: ${JSON.stringify(error)}`);
    }
    return false;
  };

  return (
    <ComponentQueryLoader
      loading={assignedWorkerLoading || staffLoading || caseStatusLoading || currentStaffLoading}
      hasData={staffData !== undefined || assignedWorkerData !== undefined}
      error={staffError || assignedWorkerError || caseStatusError || currentStaffError}
      hasDataComponent={
        <CaseDetails
          data={{
            caseData: {
              id: caseStatusData?.credentialVerificationCaseV1?.id || '',
              state: caseStatusData?.credentialVerificationCaseV1?.state || '',
              requestedAt: caseStatusData?.credentialVerificationCaseV1?.revisionRequest?.requestedAt || '',
              revisionSubmittedAt: caseStatusData?.credentialVerificationCaseV1?.revisionRequest?.revisionSubmittedAt || '',
              tags: caseStatusData?.credentialVerificationCaseV1?.tags || []
            }
          }}
          staffInfo={staffUsers}
          currentStaff={currentStaffData?.staffUserCurrent as StaffUser | undefined}
          assignedWorkerInfo={assigndWorker}
          onSave={handleSave}
          onTagsChange={handleTagsChange}
          isCurrentUser={caseStatusData?.credentialVerificationCaseV1?.isCurrentUserAssigned ?? false}
        />
      }
    />
  );
};
